import { AlertTriangle, ShieldCheck, X } from "lucide-react";

/**
 * ConfirmDialog — élégant dialogue de confirmation aux couleurs noir & or.
 * Remplace le popup natif window.confirm() jugé peu esthétique.
 *
 * Props :
 *   open        : bool                         (visible ou non)
 *   title       : string                       (titre du dialogue)
 *   message     : string | React node          (texte principal)
 *   confirmLabel: string  (défaut "Confirmer")
 *   cancelLabel : string  (défaut "Annuler")
 *   tone        : "danger" | "gold"            (couleur du bouton principal)
 *   icon        : "warning" | "shield"         (icône décorative)
 *   onConfirm   : () => void
 *   onCancel    : () => void
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  tone = "gold",
  icon = "shield",
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  const Icon = icon === "warning" ? AlertTriangle : ShieldCheck;
  const iconColor = icon === "warning" ? "#F59E0B" : "#D4AF37";

  const confirmBtnClass =
    tone === "danger"
      ? "h-11 px-6 rounded-sm text-xs uppercase tracking-[0.2em] font-semibold text-white transition-colors"
      : "btn-gold-solid h-11 px-6 rounded-sm text-xs uppercase tracking-[0.2em] font-semibold";
  const confirmBtnStyle =
    tone === "danger"
      ? { background: "#B91C1C", borderColor: "#B91C1C" }
      : {};

  return (
    <div
      data-testid="confirm-dialog"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 fade-up"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}
      onClick={(e) => e.target === e.currentTarget && onCancel && onCancel()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-md overflow-hidden"
        style={{
          background: "#0B0B0B",
          border: "1px solid rgba(212,175,55,0.35)",
          boxShadow: "0 40px 100px rgba(0,0,0,0.7)",
        }}
      >
        <div className="px-6 pt-5 pb-3 flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0"
            style={{
              background: "rgba(212,175,55,0.08)",
              border: "1px solid rgba(212,175,55,0.25)",
            }}
          >
            <Icon size={18} strokeWidth={1.6} style={{ color: iconColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-serif-display text-xl text-zinc-100 tracking-tight">
              {title}
            </div>
          </div>
          <button
            data-testid="confirm-dialog-close"
            onClick={onCancel}
            className="text-zinc-500 hover:text-[#D4AF37] transition-colors shrink-0"
            aria-label="Fermer"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </div>

        <div className="px-6 pb-5 text-[13px] text-zinc-300 leading-relaxed whitespace-pre-wrap">
          {message}
        </div>

        <div className="px-6 pb-5 flex items-center justify-end gap-3">
          <button
            data-testid="confirm-dialog-cancel"
            onClick={onCancel}
            className="h-11 px-5 rounded-sm text-xs uppercase tracking-[0.2em] font-medium text-zinc-300 border border-white/10 hover:border-white/20 hover:bg-white/[0.03] transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            data-testid="confirm-dialog-confirm"
            onClick={onConfirm}
            className={confirmBtnClass}
            style={confirmBtnStyle}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
